import React, { useEffect, useState } from 'react';
import { ResizableTable } from './ResizableTable';
import './BugReportsSection.scss';

// Mirrors the ContentReport shape written by content-report-service.
// Optional fields aren't populated on newly-filed reports until admins act.
export interface ContentReport {
    reportId: string;
    createdAt: string;
    reporterId: string;
    contentType: 'post' | 'memory';
    contentId: string;
    contentSocialNetwork: string;
    contentAuthorId?: string;
    contentAuthorHandle?: string;
    category: string;
    description?: string;
    status?: 'reported' | 'investigating' | 'resolved';
    priority?: 'normal' | 'high';
    resolution?: string;
    resolvedByAdminId?: string;
    resolvedAt?: string;
    adminNotes?: string;
    contentSnapshot?: {
        title?: string;
        descriptionLength?: number;
        mediaCount?: number;
        contentCreatedAt?: string;
    };
    updatedAt?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
    spam: 'Spam / misleading',
    harassment: 'Harassment',
    hate: 'Hate speech',
    violence: 'Violence / threats',
    self_harm: 'Self-harm / suicide',
    sexual: 'Sexual / nude',
    child_safety: 'Child safety',
    illegal: 'Illegal activity',
    ip_violation: 'IP violation',
    other: 'Other',
};

const RESOLUTION_LABELS: Record<string, string> = {
    no_action: 'No action',
    warning: 'Warning issued',
    content_removed: 'Content removed',
    account_suspended: 'Account suspended',
    other: 'Other',
};

export type ContentReportsSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

export const ContentReportsSection: React.FC<ContentReportsSectionProps> = ({ API_BASE, authHeaders }) => {
    const [reports, setReports] = useState<ContentReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedItem, setSelectedItem] = useState<ContentReport | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | 'reported' | 'investigating' | 'resolved'>('reported');

    // Modal-only fields for status/resolution/notes edits.
    const [editStatus, setEditStatus] = useState<ContentReport['status']>('reported');
    const [editResolution, setEditResolution] = useState<string>('');
    const [editNotes, setEditNotes] = useState<string>('');

    const fetchReports = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/reports`, {
                method: 'GET',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch reports.');
            }
            const data = await res.json();
            setReports(Array.isArray(data.items) ? data.items : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving reports.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReports(); }, []);

    const openDetail = (row: ContentReport) => {
        setSelectedItem(row);
        setEditStatus(row.status ?? 'reported');
        setEditResolution(row.resolution ?? '');
        setEditNotes(row.adminNotes ?? '');
        setActionError(null);
    };

    const closeDetail = () => {
        setSelectedItem(null);
        setActionError(null);
    };

    const saveDetail = async () => {
        if (!selectedItem) return;
        setActionLoading(true);
        setActionError(null);
        try {
            const body: any = {
                reportId: selectedItem.reportId,
                createdAt: selectedItem.createdAt,
                status: editStatus,
                adminNotes: editNotes,
            };
            // Only include resolution when the admin picked one — the backend
            // rejects unknown values but accepts undefined.
            if (editResolution) body.resolution = editResolution;

            const res = await fetch(`${API_BASE}/reports`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to update report.');
            }
            await fetchReports();
            closeDetail();
        } catch (err: any) {
            setActionError(err.message || 'Failed to save.');
        } finally {
            setActionLoading(false);
        }
    };

    // Row shape passed to ResizableTable — flattens categoryLabel + a compact
    // description preview so the table cells don't need custom renderers.
    const rowsAll = reports.map(r => ({
        reportId: r.reportId,
        createdAt: r.createdAt,
        status: r.status ?? 'reported',
        priority: r.priority ?? 'normal',
        categoryLabel: CATEGORY_LABELS[r.category] || r.category,
        contentType: r.contentType,
        contentAuthorHandle: r.contentAuthorHandle || '',
        contentId: r.contentId,
        description: r.description || '',
        reporterId: r.reporterId,
        // Preserve the original for the detail modal.
        __raw: r,
    }));
    const rows = statusFilter === 'all'
        ? rowsAll
        : rowsAll.filter(r => r.status === statusFilter);

    const columns = {
        createdAt: 160,
        priority: 80,
        status: 110,
        categoryLabel: 140,
        contentType: 90,
        contentAuthorHandle: 130,
        contentId: 150,
        description: 300,
        reporterId: 150,
    };

    return (
        <section className="table-section" style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <h2>Content Reports</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <label style={{ fontSize: 13 }}>Filter:</label>
                {(['reported', 'investigating', 'resolved', 'all'] as const).map(s => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        style={{
                            padding: '4px 10px',
                            border: '1px solid #d1d5db',
                            background: statusFilter === s ? '#4f46e5' : '#fff',
                            color: statusFilter === s ? '#fff' : '#374151',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 13,
                            textTransform: 'capitalize',
                        }}
                    >{s}</button>
                ))}
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>{rows.length} report{rows.length === 1 ? '' : 's'}</span>
            </div>

            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                <ResizableTable
                    columns={columns}
                    data={rows}
                    onMassActionSubmit={() => {}}
                    onRefresh={fetchReports}
                    refreshButton={true}
                    sortable={true}
                    sortableColumns={['createdAt', 'priority', 'status', 'categoryLabel', 'contentType', 'contentAuthorHandle', 'reporterId']}
                    defaultSortField="createdAt"
                    defaultSortOrder="desc"
                    submitButton={false}
                    deleteButton={false}
                    onRowClick={(row) => openDetail((row as any).__raw as ContentReport)}
                />
            </div>
            {error && <span style={{ color: 'red', marginLeft: 16 }}>{error}</span>}
            {loading && <span style={{ marginLeft: 16 }}>Loading…</span>}

            {selectedItem && (
                <div className="bug-reports-modal-overlay" onClick={closeDetail}>
                    <div className="bug-reports-modal" onClick={e => e.stopPropagation()}>
                        <h3>Content Report {selectedItem.priority === 'high' && <span style={{ color: '#dc2626', fontSize: 13, marginLeft: 8 }}>URGENT</span>}</h3>
                        <div className="bug-reports-modal-field">
                            <label>Report ID</label>
                            <p>{selectedItem.reportId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Submitted</label>
                            <p>{new Date(selectedItem.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Category</label>
                            <p>{CATEGORY_LABELS[selectedItem.category] || selectedItem.category}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Reporter user ID</label>
                            <p>{selectedItem.reporterId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Content type</label>
                            <p>{selectedItem.contentType}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Content ID</label>
                            <p>{selectedItem.contentId}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Content author handle</label>
                            <p>{selectedItem.contentAuthorHandle || '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Content author user ID</label>
                            <p>{selectedItem.contentAuthorId || '—'}</p>
                        </div>
                        <div className="bug-reports-modal-field">
                            <label>Reporter's note</label>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{selectedItem.description || '—'}</p>
                        </div>
                        {selectedItem.contentSnapshot && (
                            <div className="bug-reports-modal-field">
                                <label>Content snapshot (at report time)</label>
                                <p style={{ whiteSpace: 'pre-wrap' }}>
                                    Title: {selectedItem.contentSnapshot.title || '(none)'}{'\n'}
                                    Description length: {selectedItem.contentSnapshot.descriptionLength ?? 0}{'\n'}
                                    Media count: {selectedItem.contentSnapshot.mediaCount ?? 0}{'\n'}
                                    Content created at: {selectedItem.contentSnapshot.contentCreatedAt || 'unknown'}
                                </p>
                            </div>
                        )}

                        <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #e5e7eb' }} />

                        <div className="bug-reports-modal-field">
                            <label>Status</label>
                            <select
                                value={editStatus}
                                onChange={e => setEditStatus(e.target.value as ContentReport['status'])}
                                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                            >
                                <option value="reported">reported</option>
                                <option value="investigating">investigating</option>
                                <option value="resolved">resolved</option>
                            </select>
                        </div>
                        {editStatus === 'resolved' && (
                            <div className="bug-reports-modal-field">
                                <label>Resolution</label>
                                <select
                                    value={editResolution}
                                    onChange={e => setEditResolution(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
                                >
                                    <option value="">(choose a resolution)</option>
                                    {Object.entries(RESOLUTION_LABELS).map(([v, l]) => (
                                        <option key={v} value={v}>{l}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="bug-reports-modal-field">
                            <label>Admin notes</label>
                            <textarea
                                value={editNotes}
                                onChange={e => setEditNotes(e.target.value)}
                                rows={4}
                                maxLength={2000}
                                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: 13 }}
                                placeholder="Internal notes on the review / action taken"
                            />
                        </div>

                        {actionError && <p style={{ color: '#dc2626', fontSize: 13 }}>{actionError}</p>}

                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button className="btn btn-primary" onClick={saveDetail} disabled={actionLoading}>
                                {actionLoading ? 'Saving…' : 'Save changes'}
                            </button>
                            <button className="bug-reports-modal-close" onClick={closeDetail}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
